import React, {forwardRef, memo, useEffect, useRef} from 'react';
import MaterialTable from "material-table";
import TableIcons from "../../utils/TableIcons";
import AddBox from "@material-ui/icons/AddBox";
import {Box, Button, List, ListItem, ListItemText, TextField, Typography} from "@material-ui/core";
import SyncIcon from "@material-ui/icons/Sync";
import setDate from "../../utils/setDate";
import RefreshIcon from "@material-ui/icons/Refresh";
import getEnvironment from "../../utils/getEnvironment";
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {base16AteliersulphurpoolLight} from 'react-syntax-highlighter/dist/esm/styles/prism';
import _ from 'lodash';
import ConfigRenderInputs from "./ConfigRenderInputs";
import {isPairCategory, setConfigType, setConfigValueKey} from '../../utils/configUtils';

const ConfigTable = memo((
    {
        data,
        refetch,
        setSuccess,
        updateConfigFromTest,
        updateConfigLoading,
        createConfig,
        updateConfig,
        deleteConfig,
        finalValue = '',
        setFinalValue,
        setModalData
    }
) => {
    const env = getEnvironment();
    const tableRef = useRef();
    useEffect(() => {
        if (tableRef.current && finalValue) {
            const {dataManager, state} = tableRef.current;
            const {lastEditingRow} = state;
            if (lastEditingRow) {
                dataManager.changeRowEditing(dataManager.data[lastEditingRow.tableData.id], "update");
            }
        }
    }, [finalValue]);

    /**
     * Lookup logic
     */
    const typeLookup = {};
    data.allConfigType.enumValues.forEach(type => {
        typeLookup[type.name] = type.name;
    });
    const categoryLookup = {};
    data.allConfigCategory.enumValues.forEach(cat => {
        if (cat.name !== 'CAPACITY') {
            categoryLookup[cat.name] = cat.name;
        }
    });
    const triggerCreate = (newValue, newData) => {
        let configValue = newValue;
        const {type, category} = newData;
        let newType = type || setConfigType(category);
        if (newType === 'JSON') {
            configValue = JSON.parse(configValue);
        } else if (newType === 'DECIMAL') {
            configValue = parseFloat(configValue);
        } else if (newType === 'LIST_INT') {
            configValue = configValue.split(',').map(function (it) {
                return it.trim()
            })
        }

        const data = {
            ...newData,
            [setConfigValueKey(category, newType)]: configValue,
            category,
        };

        delete data.type;
        delete data.value;
        createConfig({
            variables: {
                input: JSON.stringify({...data})
            }
        });
    }

    const triggerUpdate = (newValue, newData) => {
        const {category, type} = newData
        let configValue = newValue;
        let newType = type || setConfigType(category);
        if (newType === 'DECIMAL') {
            configValue = parseFloat(configValue);
        } else if (newType === 'LIST_INT') {
            configValue = configValue.split(',').map(function (it) {
                return it.trim()
            })
        }

        const data = {
            ...newData,
            [setConfigValueKey(category, newType)]: configValue,
        };
        delete data.key;
        delete data.category;
        delete data.type;
        delete data.value;
        delete data.dateCreated;
        delete data.dateModified;

        updateConfig({
            variables: {
                input: JSON.stringify({...data})
            }
        });
    };

    const setDefaultList = (newValue, isPair) => {
        let defaultValue = '';
        if (typeof newValue !== typeof undefined && newValue.length) {
            newValue.forEach((val, idx) => {
                const stringVal = isPair ? val.key + ',' + val.value : val;
                if (idx === 0) {
                    defaultValue = stringVal;
                } else if (isPair) {
                    defaultValue = defaultValue + '\n' + stringVal;
                } else {
                    defaultValue = defaultValue + '\n' + val
                }
            });
        }

        return defaultValue;
    };

    return (
        <>
            <MaterialTable
                title=""
                tableRef={tableRef}
                icons={{
                    ...TableIcons,
                    Add: forwardRef((props, ref) => {
                        return (
                            <>
                                {env === 'prod' ? (
                                    <div>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSuccess(true);
                                                updateConfigFromTest();
                                            }}
                                            startIcon={<SyncIcon/>}>
                                            Sync Configurations From Test
                                        </Button>
                                    </div>
                                ) : (
                                    <AddBox {...props} ref={ref}/>
                                )}
                            </>
                        );
                    }),
                }}
                columns={[
                    {title: 'Id', field: "id", hidden: true},
                    {
                        title: "Key", field: "key", editable: 'onAdd',
                        editComponent: props => (
                            <ConfigRenderInputs
                                fieldType={"key"}
                                editProps={props}
                                data={data}
                            />
                        )
                    },
                    {
                        title: "Category", field: "category", lookup: categoryLookup, editable: 'onAdd',
                        editComponent: props => (
                            <ConfigRenderInputs
                                fieldType={"category"}
                                editProps={props}
                                data={data}
                            />
                        )
                    },
                    {
                        title: "Type", field: "type", lookup: typeLookup, editable: 'onAdd',
                        editComponent: props => (
                            <ConfigRenderInputs
                                fieldType={"type"}
                                editProps={props}
                                data={data}
                            />
                        )
                    },
                    {
                        title: "Value", field: "value",
                        editComponent: props => {
                            const {rowData, value} = props;
                            const {category, type} = rowData;
                            const categoryName = category && category.name || category;
                            const typeName = type && type.name || setConfigType(categoryName);
                            const {state} = tableRef.current;
                            const {lastEditingRow, showAddRow} = state;
                            let rowValue = finalValue || value || '';
                            if (type === 'JSON' && rowValue !== '') {
                                rowValue = JSON.stringify(rowValue);
                            }

                            let isPair = false;
                            if (categoryName) {
                                isPair = isPairCategory(categoryName)
                            }
                            if (isPair) {
                                if ((showAddRow || lastEditingRow) && finalValue) {
                                    if (typeof finalValue === 'object') {
                                        rowValue = JSON.stringify(finalValue);
                                    }
                                } else if (typeof rowValue === 'object') {
                                    rowValue = JSON.stringify(value);
                                }
                            }
                            return (
                                <TextField
                                    label={"Value"}
                                    value={rowValue}
                                    onFocus={() => {
                                        if (categoryName && (typeName === 'LIST_STRING' || typeName === 'LIST_PAIR_STRING')) {
                                            const newValue = finalValue || value;
                                            if (categoryName === 'AFFILIATE_KEY_LIST' || categoryName === 'TRAFFIC_SOURCE_ID_LIST' || categoryName === 'REFERRING_SITE_URL_LIST' || categoryName.includes('VEHICLE') || isPair) {
                                                setModalData({
                                                    modalType: 'configList',
                                                    category: categoryName,
                                                    data: rowData,
                                                    value: setDefaultList(newValue, isPair),
                                                    isPair,
                                                    showAddRow,
                                                    triggerCreate,
                                                    triggerUpdate,
                                                });
                                            } else if (categoryName && categoryName === 'STATE_LIST') {
                                                setModalData({
                                                    modalType: 'configState',
                                                    category: categoryName,
                                                    data: rowData,
                                                    value: setDefaultList(newValue, isPair),
                                                    isPair,
                                                    showAddRow,
                                                    triggerCreate,
                                                    triggerUpdate,
                                                });
                                            }
                                        } else if (categoryName && (categoryName === 'SCHEDULE' || categoryName.includes('CAPACITY_'))) {
                                            setModalData({
                                                modalType: categoryName === 'SCHEDULE' ? 'configSchedule' : 'configCapacity',
                                                category: categoryName,
                                                data: rowData,
                                                value: rowValue ? JSON.parse(rowValue) : '',
                                                showAddRow,
                                                triggerCreate,
                                                triggerUpdate,
                                            });
                                        }
                                    }}
                                    onChange={e => {
                                        props.onChange(e.target.value);
                                    }}
                                    error={!value}
                                />
                            );
                        },
                        render: (rowData) => {
                            const {type, value, category} = rowData;
                            const enabledList = type === 'LIST_INT' || type === 'LIST_STRING' || type === 'LIST_PAIR_STRING';
                            if (type === 'JSON') {
                                let newJSON = value;
                                if (_.has(newJSON, '__typename')) {
                                    delete newJSON['__typename'];
                                }
                                // Remove Unused variables
                                if (category === 'SCHEDULE') {
                                    const available = newJSON.available;
                                    available.forEach(item => {
                                        delete item['__typename'];
                                        item.ranges.forEach(time => {
                                            delete time['__typename'];
                                        })
                                    });
                                    newJSON = {
                                        ...newJSON,
                                        available: {
                                            ...available
                                        }
                                    };
                                }
                                return (
                                    <Box style={{height: '250px', overflow: 'scroll'}}>
                                        <SyntaxHighlighter language="json" style={base16AteliersulphurpoolLight}>
                                            {JSON.stringify(newJSON, null, 1)}
                                        </SyntaxHighlighter>
                                    </Box>
                                );
                            }

                            if (type === 'INT' || type === 'DECIMAL') {
                                return (
                                    <>
                                        {!value ? 0 : value}
                                    </>
                                );
                            }

                            return (
                                <>
                                    {!enabledList ? (
                                        <>
                                            {value.toString()}
                                        </>
                                    ) : (
                                        <List dense style={{height: '250px', overflow: 'scroll'}}>
                                            {value.map((item, index) => {
                                                let key = type === 'LIST_PAIR_STRING' ? item.key : item;
                                                return (
                                                    <ListItem key={`${key}_${index}`} divider alignItems={"flex-start"}
                                                              disableGutters>
                                                        <ListItemText disableTypography>
                                                            <Typography variant="body2">
                                                                {type === 'LIST_PAIR_STRING' ? (
                                                                    <>
                                                                        <strong>{category.includes("AFFILIATE") ? "Affiliate Key" : "UUID"}: </strong> {item.key} <br/> <br/>
                                                                        <strong> {category.includes("SUBCODE") ? "Subcode" : "Value"}: </strong> {item.value}
                                                                    </>
                                                                ) : (
                                                                    <>{item}</>
                                                                )}
                                                            </Typography>
                                                        </ListItemText>
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    )}
                                </>
                            );
                        }
                    },
                    {title: "Date Created", field: "dateCreated", editable: "never", filtering: false},
                    {title: "Date Modified", field: "dateModified", editable: "never", filtering: false},
                ]}
                data={data.allConfig.nodes.map(config => {
                    let value
                    if (config.valueBool != null) {
                        value = config.valueBool
                    } else if (config.valueDecimal) {
                        value = parseFloat(config.valueDecimal);
                    } else {
                        value = config.valueInt || config.valueIntArray || config.valueString || config.valueStringArray || config.valuePairStringStringArray || config.valueCampaignSchedule || config.valueCampaignCapacity
                    }
                    return {
                        id: config.id,
                        key: config.key,
                        category: config.category,
                        type: config.type,
                        value: value,
                        dateCreated: setDate(config.dateCreated),
                        dateModified: setDate(config.dateModified)
                    }
                })}
                editable={{
                    onRowAdd: newData =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                const {category, key, value} = newData;
                                const newValue = finalValue || value;
                                if (!category || !key || !newValue) {
                                    alert('Please fill out all required fields');
                                    reject();
                                } else {
                                    triggerCreate(newValue, newData);
                                    resolve();
                                }

                            }, 1000);
                        }),
                    onRowUpdate: env === 'test' ? (newData, oldData) =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                const {category, type, key, value} = newData;
                                const newValue = finalValue || value;
                                if (!category || !key || !value) {
                                    alert('Please fill out all required fields');
                                    reject();
                                } else {
                                    triggerUpdate(newValue, newData);
                                    resolve();
                                }
                            }, 1000);
                        }) : null,
                    onRowDelete: env === 'test' ? oldData =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                deleteConfig({
                                    variables: {
                                        id: oldData.id
                                    }
                                });
                                resolve();
                            }, 1000)
                        }) : null,
                    onRowAddCancelled: () => setFinalValue(''),
                    onRowUpdateCancelled: () => setFinalValue(''),
                }}
                actions={[
                    {
                        icon: () => <RefreshIcon/>,
                        tooltip: 'Refetch Data',
                        isFreeAction: true,
                        onClick: () => refetch()
                    },
                ]}
                options={{
                    addRowPosition: 'first',
                    filtering: true,
                    pageSize: 300,
                    pageSizeOptions: [300, 600, 800],
                    headerStyle: {
                        minWidth: 'auto',
                    },
                    cellStyle: {
                        minWidth: 350
                    }
                }}
            />
        </>
    );
});

export default ConfigTable;
